import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { MEDIA, useIsMobile } from '@brg/ui'
import { ProjectSearchBarSorting } from 'shared/components/projectsSearchBar/components/projectSearchBarSorting'
import { SortingTypeIndex } from 'shared/constants/sortingType'
import { SortIcon } from 'shared/icons'
import {
  TEST_ATTEMPT_FEEDBACK_GREEN,
  TEST_ATTEMPT_FEEDBACK_RED
} from 'shared/pages/testsAdmin/components/TestAttemptProgressBar'
import type { AnswerMode, SurveyQuestionKind } from 'shared/modals/testCreate/questionTypes'
import {
  SavedOptionMark,
  UiCheckboxWrap,
} from 'shared/modals/testCreate/questionsTabStyles'
import { CommonCheckbox } from 'shared/components/CommonCheckbox/CommonCheckbox'

export type TestAttemptOptionTone = 'correct' | 'incorrect' | 'neutral'

export type TestAttemptQuestionTone = 'correct' | 'incorrect' | 'partial' | 'neutral'

export type TestAttemptOption = {
  id: string
  title: string
  description: string
  imageUrl?: string
  tone: TestAttemptOptionTone
  selected: boolean
}

export type TestAttemptMatrixCell = {
  rowId: string
  rowLabel: string
  value: string
}

export type TestAttemptQuestion = {
  id: string
  index: number
  title: string
  tone: TestAttemptQuestionTone
  answerMode: AnswerMode
  surveyKind: SurveyQuestionKind
  options: TestAttemptOption[]
  selectedText?: string
  selectedScale?: number | null
  /** 5 | 10 for scale/matrix read-only cells */
  scaleMax?: 5 | 10
  matrixAnswers?: TestAttemptMatrixCell[]
  /** Пояснение к правильному ответу (под списком вариантов) */
  explanation?: string
}

const OPTION_BORDER_NEUTRAL = '#b6b6b5'

function questionBadgeBackground(tone: TestAttemptQuestionTone): string {
  if (tone === 'correct') {
    return TEST_ATTEMPT_FEEDBACK_GREEN
  }
  if (tone === 'incorrect') {
    return TEST_ATTEMPT_FEEDBACK_RED
  }
  return '#8d8d8d'
}

const QuestionsHeaderRow = styled.div`
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  @media ${MEDIA.MOBILE} {
    gap: 12px;
    justify-content: flex-end;
  }
`

const QuestionsTitleRow = styled.div`
  flex: 1;
  min-width: 0;

  @media ${MEDIA.MOBILE} {
    flex-basis: 100%;
    border-bottom: 1px solid #cdcdcd;
    padding-bottom: 8px;
  }
`

const QuestionsTitle = styled.h2`
  margin: 0;
  font-size: 30px;
  line-height: 40px;
  font-weight: 600;
  color: #151514;

  @media ${MEDIA.TABLET} {
    font-size: 23px;
    line-height: 27px;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 20px;
    line-height: 25px;
  }
`

const SortingSlot = styled.div`
  min-width: 210px;
  display: flex;
  justify-content: flex-end;
`

const MobileFilterSort = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 15px;
  line-height: 20px;
  color: #909090;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`

const QuestionsTitleOnlyHeader = styled.div`
  margin-top: 24px;
`

const QuestionsStack = styled.div`
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const QuestionCard = styled.article<{ $survey?: boolean }>`
  background: #f8f9fe;
  border-radius: 8px;
  padding: ${(p) => (p.$survey ? '32px 48px' : '16px')};

  @media ${MEDIA.TABLET} {
    padding: ${(p) => (p.$survey ? '24px 32px' : '16px')};
  }

  @media ${MEDIA.MOBILE} {
    padding: 16px;
    border-radius: 0;
  }
`

const QuestionTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`

const QuestionNumber = styled.div`
  color: #909090;
  font-size: 17px;
  line-height: 22px;
  font-weight: 400;

  @media ${MEDIA.TABLET} {
    font-size: 15px;
    line-height: 20px;
  }
`

const QuestionBadge = styled.span<{ $tone: TestAttemptQuestionTone }>`
  border-radius: 4px;
  padding: 3px 10px;
  color: #fff;
  font-size: 10px;
  line-height: 13px;
  font-weight: 600;
  background: ${(p) => questionBadgeBackground(p.$tone)};
`

const QuestionTitle = styled.h3`
  margin: 8px 0 12px;
  font-size: 26px;
  line-height: 32px;
  font-weight: 600;
  color: #151514;
  overflow-wrap: anywhere;
  word-break: break-word;

  @media ${MEDIA.TABLET} {
    font-size: 22px;
    line-height: 28px;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 17px;
    line-height: 22px;
  }
`

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const OptionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  min-width: 0;

  @media ${MEDIA.MOBILE} {
    flex-direction: column;
    gap: 12px;
  }
`

const OptionImageCell = styled.div`
  flex-shrink: 0;
  width: 270px;
  height: 182px;
  border-radius: 4px;
  overflow: hidden;
  background: #e7e7e7;

  @media ${MEDIA.LAPTOP} {
    width: 204px;
    height: 138px;
  }

  @media ${MEDIA.TABLET} {
    width: 166px;
    height: 112px;
  }

  @media ${MEDIA.MOBILE} {
    width: 100%;
    height: 100%;
  }
`

const OptionCard = styled.div<{ $tone: TestAttemptOptionTone }>`
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  border-radius: 6px;
  padding: 14px 16px;
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: 10px;
  align-items: start;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;

  ${(p) => {
    if (p.$tone === 'correct') {
      return `
        border: 2px solid ${TEST_ATTEMPT_FEEDBACK_GREEN};
        background: #f8f9fe;
      `
    }
    if (p.$tone === 'incorrect') {
      return `
        border: 2px solid ${TEST_ATTEMPT_FEEDBACK_RED};
        background: #f8f9fe;
      `
    }
    return `
      border: 1px solid ${OPTION_BORDER_NEUTRAL};
      background: #f8f9fe;
    `
  }}

  @media ${MEDIA.MOBILE} {
    width: 100%;
  }
`

const QuestionAnswerModeHint = styled.span`
  display: block;
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 16px;
  color: #909090;
  font-weight: 400;

  @media ${MEDIA.TABLET} {
    font-size: 11px;
    line-height: 13px;
  }
`

const OptionTitle = styled.div`
  font-size: 17px;
  line-height: 22px;
  font-weight: 600;
  color: #151514;

  @media ${MEDIA.TABLET} {
    font-size: 15px;
    line-height: 20px;
  }
`

const OptionDescription = styled.div`
  margin-top: 2px;
  font-size: 17px;
  line-height: 22px;
  color: #909090;
  font-weight: 400;

  @media ${MEDIA.TABLET} {
    font-size: 15px;
    line-height: 20px;
  }
`

const QuestionExplanation = styled.div`
  margin-top: 40px;
  font-size: 17px;
  line-height: 22px;
  color: #909090;
  font-weight: 400;
  white-space: pre-wrap;

  @media ${MEDIA.TABLET} {
    margin-top: 24px;
    font-size: 15px;
    line-height: 20px;
  }

  @media ${MEDIA.MOBILE} {
    margin-top: 16px;
  }
`

const OptionImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const questionBadgeText: Record<TestAttemptQuestionTone, string> = {
  correct: 'Верный ответ',
  incorrect: 'Неверный ответ',
  partial: 'Частично верно',
  neutral: '',
}

const SurveyAnswerText = styled.div<{ $long?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  max-width: 904px;
  font-family: Inter, sans-serif;
  font-size: ${(p) => (p.$long ? '20px' : '20px')};
  line-height: 28px;
  letter-spacing: -0.8px;
  font-weight: 400;
  color: #151514;
  white-space: pre-wrap;
  word-break: break-word;

  @media ${MEDIA.TABLET} {
    font-size: 17px;
    line-height: 22px;
    letter-spacing: -0.34px;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 15px;
    line-height: 20px;
    letter-spacing: -0.3px;
  }
`

const SurveyScaleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 904px;

  @media ${MEDIA.MOBILE} {
    gap: 4px;
  }
`

const SurveyScaleCell = styled.div<{ $selected?: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  box-sizing: border-box;
  border-radius: 3px;
  border: ${(p) => (p.$selected ? 'none' : '1px solid #909090')};
  background: ${(p) => (p.$selected ? '#343434' : 'transparent')};
  color: ${(p) => (p.$selected ? '#ffffff' : '#545453')};
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.3px;
  text-align: center;

  @media ${MEDIA.MOBILE} {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: 0;
  }
`

const SurveyMatrixList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 904px;

  @media ${MEDIA.MOBILE} {
    gap: 16px;
  }
`

const SurveyMatrixRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  width: 100%;

  @media ${MEDIA.MOBILE} {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`

const SurveyMatrixLabel = styled.span`
  flex-shrink: 0;
  width: 250px;
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: -0.3px;
  font-weight: 600;
  color: #545453;
  overflow-wrap: anywhere;
  word-break: break-word;

  @media ${MEDIA.TABLET} {
    width: 180px;
  }

  @media ${MEDIA.MOBILE} {
    width: 100%;
  }
`

const SurveyMatrixCells = styled.div`
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  align-items: center;
  gap: 16px;

  @media ${MEDIA.MOBILE} {
    gap: 4px;
  }
`

const SurveyMatrixDivider = styled.div`
  width: 100%;
  height: 1px;
  background: #909090;

  @media ${MEDIA.MOBILE} {
    display: none;
  }
`

const SurveyEmptyHint = styled.div`
  color: #909090;
  font-size: 15px;
  line-height: 20px;
`

type Props = {
  questions: TestAttemptQuestion[]
  hideSorting?: boolean
  /** Questionnaire attempt: no correct/incorrect, only selected answers / new kinds. */
  surveyMode?: boolean
}

function buildScaleValues(scaleMax: 5 | 10): number[] {
  return Array.from({ length: scaleMax }, (_, i) => i + 1)
}

function resolveQuestionScaleMax(q: TestAttemptQuestion): 5 | 10 {
  if (q.scaleMax === 10 || q.scaleMax === 5) {
    return q.scaleMax
  }
  if (q.selectedScale != null && q.selectedScale > 5) {
    return 10
  }
  if ((q.matrixAnswers || []).some((c) => Number(c.value) > 5)) {
    return 10
  }
  return 5
}

export function TestAttemptQuestionsSection({
  questions,
  hideSorting = false,
  surveyMode = false,
}: Props) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const [questionSorting, setQuestionSorting] = useState<SortingTypeIndex>(
    SortingTypeIndex.moderation
  )

  const sortedQuestions = useMemo(() => {
    const list = [...questions]
    if (hideSorting) {
      list.sort((a, b) => a.index - b.index)
      return list
    }
    list.sort((a, b) => {
      if (questionSorting === SortingTypeIndex.popular) {
        return b.index - a.index
      }
      return a.index - b.index
    })
    return list
  }, [hideSorting, questionSorting, questions])

  if (!questions.length) {
    return null
  }

  return (
    <>
      {hideSorting ? (
        <QuestionsTitleOnlyHeader>
          <QuestionsTitle>Вопросы</QuestionsTitle>
        </QuestionsTitleOnlyHeader>
      ) : (
        <QuestionsHeaderRow>
          <QuestionsTitleRow>
            <QuestionsTitle>Вопросы</QuestionsTitle>
          </QuestionsTitleRow>
          <SortingSlot>
            {isMobile ? (
              <MobileFilterSort
                onClick={() =>
                  setQuestionSorting((prev) =>
                    prev === SortingTypeIndex.moderation
                      ? SortingTypeIndex.popular
                      : SortingTypeIndex.moderation
                  )
                }
              >
                <SortIcon />
                {t(
                  `filter.${
                    questionSorting === SortingTypeIndex.moderation
                      ? 'moderation_nom'
                      : 'popular_nom'
                  }`
                )}
              </MobileFilterSort>
            ) : (
              <ProjectSearchBarSorting
                selectedSorting={questionSorting}
                onSortingChange={setQuestionSorting}
                sortingMethods={[SortingTypeIndex.moderation, SortingTypeIndex.popular]}
              />
            )}
          </SortingSlot>
        </QuestionsHeaderRow>
      )}

      <QuestionsStack>
        {sortedQuestions.map((q) => {
          const kind = q.surveyKind || (q.answerMode === 'multiple' ? 'multiple' : 'single')
          const isInfo = surveyMode && kind === 'info'
          const isText =
            surveyMode && (kind === 'text_short' || kind === 'text_long')
          const isScale = surveyMode && kind === 'scale'
          const isMatrix = surveyMode && kind === 'matrix'
          const visibleOptions = surveyMode
            ? q.options.filter((o) => o.selected)
            : q.options
          const scaleMax = resolveQuestionScaleMax(q)
          const scaleValues = buildScaleValues(scaleMax)

          return (
            <QuestionCard key={q.id} $survey={surveyMode}>
              <QuestionTop>
                <QuestionNumber>Вопрос {q.index}</QuestionNumber>
                {!surveyMode && q.tone !== 'neutral' ? (
                  <QuestionBadge $tone={q.tone}>{questionBadgeText[q.tone]}</QuestionBadge>
                ) : null}
              </QuestionTop>
              <QuestionTitle>{q.title}</QuestionTitle>
              {!surveyMode ? (
                <QuestionAnswerModeHint>
                  {q.answerMode === 'multiple'
                    ? t('modal.test_create.questions.multiple_correct')
                    : t('modal.test_create.questions.single_correct')}
                </QuestionAnswerModeHint>
              ) : null}

              {isInfo ? (
                <SurveyAnswerText>
                  {q.options.some((o) => o.selected)
                    ? t('tests_admin.attempt_info_viewed')
                    : '—'}
                </SurveyAnswerText>
              ) : null}

              {isText ? (
                <SurveyAnswerText $long={kind === 'text_long'}>
                  {q.selectedText?.trim() || '—'}
                </SurveyAnswerText>
              ) : null}

              {isScale ? (
                q.selectedScale != null && Number.isFinite(q.selectedScale) ? (
                  <SurveyScaleRow>
                    {scaleValues.map((n) => (
                      <SurveyScaleCell
                        key={`${q.id}-s-${n}`}
                        $selected={n === q.selectedScale}
                      >
                        {n}
                      </SurveyScaleCell>
                    ))}
                  </SurveyScaleRow>
                ) : (
                  <SurveyEmptyHint>—</SurveyEmptyHint>
                )
              ) : null}

              {isMatrix ? (
                <SurveyMatrixList>
                  {(q.matrixAnswers || []).length === 0 ? (
                    <SurveyEmptyHint>—</SurveyEmptyHint>
                  ) : (
                    (q.matrixAnswers || []).map((cell, cellIdx) => (
                      <React.Fragment key={`${q.id}-${cell.rowId}`}>
                        {cellIdx > 0 ? <SurveyMatrixDivider /> : null}
                        <SurveyMatrixRow>
                          <SurveyMatrixLabel>{cell.rowLabel}</SurveyMatrixLabel>
                          <SurveyMatrixCells>
                            {scaleValues.map((n) => (
                              <SurveyScaleCell
                                key={`${q.id}-${cell.rowId}-${n}`}
                                $selected={String(n) === String(cell.value)}
                              >
                                {n}
                              </SurveyScaleCell>
                            ))}
                          </SurveyMatrixCells>
                        </SurveyMatrixRow>
                      </React.Fragment>
                    ))
                  )}
                </SurveyMatrixList>
              ) : null}

              {!isInfo && !isText && !isScale && !isMatrix ? (
                <OptionList>
                  {visibleOptions.length === 0 && surveyMode ? (
                    <SurveyEmptyHint>—</SurveyEmptyHint>
                  ) : (
                    visibleOptions.map((option) => (
                      <OptionRow key={option.id}>
                        {option.imageUrl ? (
                          <OptionImageCell>
                            <OptionImage src={option.imageUrl} alt="" loading="lazy" />
                          </OptionImageCell>
                        ) : null}
                        <OptionCard $tone={surveyMode ? 'neutral' : option.tone}>
                          {q.answerMode === 'single' ||
                          kind === 'dropdown' ||
                          kind === 'single' ? (
                            <SavedOptionMark
                              type="radio"
                              name={`attempt-${q.id}`}
                              checked={option.selected}
                              readOnly
                              tabIndex={-1}
                              aria-hidden
                            />
                          ) : (
                            <UiCheckboxWrap>
                              <CommonCheckbox
                                checked={option.selected}
                                disabled
                                onClick={() => {}}
                              />
                            </UiCheckboxWrap>
                          )}
                          <div>
                            <OptionTitle>{option.title}</OptionTitle>
                            {option.description.trim() !== '' ? (
                              <OptionDescription>{option.description}</OptionDescription>
                            ) : null}
                          </div>
                        </OptionCard>
                      </OptionRow>
                    ))
                  )}
                </OptionList>
              ) : null}

              {!surveyMode && q.explanation && q.explanation.trim() !== '' ? (
                <QuestionExplanation>{q.explanation}</QuestionExplanation>
              ) : null}
            </QuestionCard>
          )
        })}
      </QuestionsStack>
    </>
  )
}
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { MEDIA, useIsMobile } from '@brg/ui'
import { ProjectSearchBarSorting } from 'shared/components/projectsSearchBar/components/projectSearchBarSorting'
import { SortingTypeIndex } from 'shared/constants/sortingType'
import { SortIcon } from 'shared/icons'
import {
  TEST_ATTEMPT_FEEDBACK_GREEN,
  TEST_ATTEMPT_FEEDBACK_RED
} from 'shared/pages/testsAdmin/components/TestAttemptProgressBar'
import type { AnswerMode, SurveyQuestionKind } from 'shared/modals/testCreate/questionTypes'
import {
  SavedOptionMark,
  UiCheckboxWrap,
} from 'shared/modals/testCreate/questionsTabStyles'
import { CommonCheckbox } from 'shared/components/CommonCheckbox/CommonCheckbox'

export type TestAttemptOptionTone = 'correct' | 'incorrect' | 'neutral'

export type TestAttemptQuestionTone = 'correct' | 'incorrect' | 'partial' | 'neutral'

export type TestAttemptOption = {
  id: string
  title: string
  description: string
  imageUrl?: string
  tone: TestAttemptOptionTone
  selected: boolean
}

export type TestAttemptMatrixCell = {
  rowId: string
  rowLabel: string
  value: string
}

export type TestAttemptQuestion = {
  id: string
  index: number
  title: string
  tone: TestAttemptQuestionTone
  answerMode: AnswerMode
  surveyKind: SurveyQuestionKind
  options: TestAttemptOption[]
  selectedText?: string
  selectedScale?: number | null
  /** 5 | 10 for scale/matrix read-only cells */
  scaleMax?: 5 | 10
  matrixAnswers?: TestAttemptMatrixCell[]
  /** Пояснение к правильному ответу (под списком вариантов) */
  explanation?: string
}

const OPTION_BORDER_NEUTRAL = '#b6b6b5'

function questionBadgeBackground(tone: TestAttemptQuestionTone): string {
  if (tone === 'correct') {
    return TEST_ATTEMPT_FEEDBACK_GREEN
  }
  if (tone === 'incorrect') {
    return TEST_ATTEMPT_FEEDBACK_RED
  }
  return '#8d8d8d'
}

const QuestionsHeaderRow = styled.div`
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  @media ${MEDIA.MOBILE} {
    gap: 12px;
    justify-content: flex-end;
  }
`

const QuestionsTitleRow = styled.div`
  flex: 1;
  min-width: 0;

  @media ${MEDIA.MOBILE} {
    flex-basis: 100%;
    border-bottom: 1px solid #cdcdcd;
    padding-bottom: 8px;
  }
`

const QuestionsTitle = styled.h2`
  margin: 0;
  font-size: 30px;
  line-height: 40px;
  font-weight: 600;
  color: #151514;

  @media ${MEDIA.TABLET} {
    font-size: 23px;
    line-height: 27px;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 20px;
    line-height: 25px;
  }
`

const SortingSlot = styled.div`
  min-width: 210px;
  display: flex;
  justify-content: flex-end;
`

const MobileFilterSort = styled.div`
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 15px;
  line-height: 20px;
  color: #909090;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`

const QuestionsTitleOnlyHeader = styled.div`
  margin-top: 24px;
`

const QuestionsStack = styled.div`
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const QuestionCard = styled.article<{ $survey?: boolean }>`
  background: #f8f9fe;
  border-radius: 8px;
  padding: ${(p) => (p.$survey ? '32px 48px' : '16px')};

  @media ${MEDIA.TABLET} {
    padding: ${(p) => (p.$survey ? '24px 32px' : '16px')};
  }

  @media ${MEDIA.MOBILE} {
    padding: 16px;
    border-radius: 0;
  }
`

const QuestionTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`

const QuestionNumber = styled.div`
  color: #909090;
  font-size: 17px;
  line-height: 22px;
  font-weight: 400;

  @media ${MEDIA.TABLET} {
    font-size: 15px;
    line-height: 20px;
  }
`

const QuestionBadge = styled.span<{ $tone: TestAttemptQuestionTone }>`
  border-radius: 4px;
  padding: 3px 10px;
  color: #fff;
  font-size: 10px;
  line-height: 13px;
  font-weight: 600;
  background: ${(p) => questionBadgeBackground(p.$tone)};
`

const QuestionTitle = styled.h3`
  margin: 8px 0 12px;
  font-size: 26px;
  line-height: 32px;
  font-weight: 600;
  color: #151514;
  overflow-wrap: anywhere;
  word-break: break-word;

  @media ${MEDIA.TABLET} {
    font-size: 22px;
    line-height: 28px;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 17px;
    line-height: 22px;
  }
`

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const OptionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  min-width: 0;

  @media ${MEDIA.MOBILE} {
    flex-direction: column;
    gap: 12px;
  }
`

const OptionImageCell = styled.div`
  flex-shrink: 0;
  width: 270px;
  height: 182px;
  border-radius: 4px;
  overflow: hidden;
  background: #e7e7e7;

  @media ${MEDIA.LAPTOP} {
    width: 204px;
    height: 138px;
  }

  @media ${MEDIA.TABLET} {
    width: 166px;
    height: 112px;
  }

  @media ${MEDIA.MOBILE} {
    width: 100%;
    height: 100%;
  }
`

const OptionCard = styled.div<{ $tone: TestAttemptOptionTone }>`
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  border-radius: 6px;
  padding: 14px 16px;
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: 10px;
  align-items: start;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;

  ${(p) => {
    if (p.$tone === 'correct') {
      return `
        border: 2px solid ${TEST_ATTEMPT_FEEDBACK_GREEN};
        background: #f8f9fe;
      `
    }
    if (p.$tone === 'incorrect') {
      return `
        border: 2px solid ${TEST_ATTEMPT_FEEDBACK_RED};
        background: #f8f9fe;
      `
    }
    return `
      border: 1px solid ${OPTION_BORDER_NEUTRAL};
      background: #f8f9fe;
    `
  }}

  @media ${MEDIA.MOBILE} {
    width: 100%;
  }
`

const QuestionAnswerModeHint = styled.span`
  display: block;
  margin: 0 0 12px;
  font-size: 13px;
  line-height: 16px;
  color: #909090;
  font-weight: 400;

  @media ${MEDIA.TABLET} {
    font-size: 11px;
    line-height: 13px;
  }
`

const OptionTitle = styled.div`
  font-size: 17px;
  line-height: 22px;
  font-weight: 600;
  color: #151514;

  @media ${MEDIA.TABLET} {
    font-size: 15px;
    line-height: 20px;
  }
`

const OptionDescription = styled.div`
  margin-top: 2px;
  font-size: 17px;
  line-height: 22px;
  color: #909090;
  font-weight: 400;

  @media ${MEDIA.TABLET} {
    font-size: 15px;
    line-height: 20px;
  }
`

const QuestionExplanation = styled.div`
  margin-top: 40px;
  font-size: 17px;
  line-height: 22px;
  color: #909090;
  font-weight: 400;
  white-space: pre-wrap;

  @media ${MEDIA.TABLET} {
    margin-top: 24px;
    font-size: 15px;
    line-height: 20px;
  }

  @media ${MEDIA.MOBILE} {
    margin-top: 16px;
  }
`

const OptionImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const questionBadgeText: Record<TestAttemptQuestionTone, string> = {
  correct: 'Верный ответ',
  incorrect: 'Неверный ответ',
  partial: 'Частично верно',
  neutral: '',
}

const SurveyAnswerText = styled.div<{ $long?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  max-width: 904px;
  font-family: Inter, sans-serif;
  font-size: ${(p) => (p.$long ? '20px' : '20px')};
  line-height: 28px;
  letter-spacing: -0.8px;
  font-weight: 400;
  color: #151514;
  white-space: pre-wrap;
  word-break: break-word;

  @media ${MEDIA.TABLET} {
    font-size: 17px;
    line-height: 22px;
    letter-spacing: -0.34px;
  }

  @media ${MEDIA.MOBILE} {
    font-size: 15px;
    line-height: 20px;
    letter-spacing: -0.3px;
  }
`

const SurveyScaleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 904px;

  @media ${MEDIA.MOBILE} {
    gap: 4px;
  }
`

const SurveyScaleCell = styled.div<{ $selected?: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  box-sizing: border-box;
  border-radius: 3px;
  border: ${(p) => (p.$selected ? 'none' : '1px solid #909090')};
  background: ${(p) => (p.$selected ? '#343434' : 'transparent')};
  color: ${(p) => (p.$selected ? '#ffffff' : '#545453')};
  font-family: Inter, sans-serif;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.3px;
  text-align: center;

  @media ${MEDIA.MOBILE} {
    font-size: 13px;
    line-height: 16px;
    letter-spacing: 0;
  }
`

const SurveyMatrixList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 904px;

  @media ${MEDIA.MOBILE} {
    gap: 16px;
  }
`

const SurveyMatrixRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 24px;
  width: 100%;

  @media ${MEDIA.MOBILE} {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`

const SurveyMatrixLabel = styled.span`
  flex-shrink: 0;
  width: 250px;
  font-family: Inter, sans-serif;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: -0.3px;
  font-weight: 600;
  color: #545453;
  overflow-wrap: anywhere;
  word-break: break-word;

  @media ${MEDIA.TABLET} {
    width: 180px;
  }

  @media ${MEDIA.MOBILE} {
    width: 100%;
  }
`

const SurveyMatrixCells = styled.div`
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  align-items: center;
  gap: 16px;

  @media ${MEDIA.MOBILE} {
    gap: 4px;
  }
`

const SurveyMatrixDivider = styled.div`
  width: 100%;
  height: 1px;
  background: #909090;

  @media ${MEDIA.MOBILE} {
    display: none;
  }
`

const SurveyEmptyHint = styled.div`
  color: #909090;
  font-size: 15px;
  line-height: 20px;
`

type Props = {
  questions: TestAttemptQuestion[]
  hideSorting?: boolean
  /** Questionnaire attempt: no correct/incorrect, only selected answers / new kinds. */
  surveyMode?: boolean
}

function buildScaleValues(scaleMax: 5 | 10): number[] {
  return Array.from({ length: scaleMax }, (_, i) => i + 1)
}

function resolveQuestionScaleMax(q: TestAttemptQuestion): 5 | 10 {
  if (q.scaleMax === 10 || q.scaleMax === 5) {
    return q.scaleMax
  }
  if (q.selectedScale != null && q.selectedScale > 5) {
    return 10
  }
  if ((q.matrixAnswers || []).some((c) => Number(c.value) > 5)) {
    return 10
  }
  return 5
}

export function TestAttemptQuestionsSection({
  questions,
  hideSorting = false,
  surveyMode = false,
}: Props) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const [questionSorting, setQuestionSorting] = useState<SortingTypeIndex>(
    SortingTypeIndex.moderation
  )

  const sortedQuestions = useMemo(() => {
    const list = [...questions]
    if (hideSorting) {
      list.sort((a, b) => a.index - b.index)
      return list
    }
    list.sort((a, b) => {
      if (questionSorting === SortingTypeIndex.popular) {
        return b.index - a.index
      }
      return a.index - b.index
    })
    return list
  }, [hideSorting, questionSorting, questions])

  if (!questions.length) {
    return null
  }

  return (
    <>
      {hideSorting ? (
        <QuestionsTitleOnlyHeader>
          <QuestionsTitle>Вопросы</QuestionsTitle>
        </QuestionsTitleOnlyHeader>
      ) : (
        <QuestionsHeaderRow>
          <QuestionsTitleRow>
            <QuestionsTitle>Вопросы</QuestionsTitle>
          </QuestionsTitleRow>
          <SortingSlot>
            {isMobile ? (
              <MobileFilterSort
                onClick={() =>
                  setQuestionSorting((prev) =>
                    prev === SortingTypeIndex.moderation
                      ? SortingTypeIndex.popular
                      : SortingTypeIndex.moderation
                  )
                }
              >
                <SortIcon />
                {t(
                  `filter.${
                    questionSorting === SortingTypeIndex.moderation
                      ? 'moderation_nom'
                      : 'popular_nom'
                  }`
                )}
              </MobileFilterSort>
            ) : (
              <ProjectSearchBarSorting
                selectedSorting={questionSorting}
                onSortingChange={setQuestionSorting}
                sortingMethods={[SortingTypeIndex.moderation, SortingTypeIndex.popular]}
              />
            )}
          </SortingSlot>
        </QuestionsHeaderRow>
      )}

      <QuestionsStack>
        {sortedQuestions.map((q) => {
          const kind = q.surveyKind || (q.answerMode === 'multiple' ? 'multiple' : 'single')
          const isInfo = surveyMode && kind === 'info'
          const isText =
            surveyMode && (kind === 'text_short' || kind === 'text_long')
          const isScale = surveyMode && kind === 'scale'
          const isMatrix = surveyMode && kind === 'matrix'
          const visibleOptions = surveyMode
            ? q.options.filter((o) => o.selected)
            : q.options
          const scaleMax = resolveQuestionScaleMax(q)
          const scaleValues = buildScaleValues(scaleMax)

          return (
            <QuestionCard key={q.id} $survey={surveyMode}>
              <QuestionTop>
                <QuestionNumber>Вопрос {q.index}</QuestionNumber>
                {!surveyMode && q.tone !== 'neutral' ? (
                  <QuestionBadge $tone={q.tone}>{questionBadgeText[q.tone]}</QuestionBadge>
                ) : null}
              </QuestionTop>
              <QuestionTitle>{q.title}</QuestionTitle>
              {!surveyMode ? (
                <QuestionAnswerModeHint>
                  {q.answerMode === 'multiple'
                    ? t('modal.test_create.questions.multiple_correct')
                    : t('modal.test_create.questions.single_correct')}
                </QuestionAnswerModeHint>
              ) : null}

              {isInfo ? (
                <SurveyAnswerText>
                  {q.options.some((o) => o.selected)
                    ? t('tests_admin.attempt_info_viewed')
                    : '—'}
                </SurveyAnswerText>
              ) : null}

              {isText ? (
                <SurveyAnswerText $long={kind === 'text_long'}>
                  {q.selectedText?.trim() || '—'}
                </SurveyAnswerText>
              ) : null}

              {isScale ? (
                q.selectedScale != null && Number.isFinite(q.selectedScale) ? (
                  <SurveyScaleRow>
                    {scaleValues.map((n) => (
                      <SurveyScaleCell
                        key={`${q.id}-s-${n}`}
                        $selected={n === q.selectedScale}
                      >
                        {n}
                      </SurveyScaleCell>
                    ))}
                  </SurveyScaleRow>
                ) : (
                  <SurveyEmptyHint>—</SurveyEmptyHint>
                )
              ) : null}

              {isMatrix ? (
                <SurveyMatrixList>
                  {(q.matrixAnswers || []).length === 0 ? (
                    <SurveyEmptyHint>—</SurveyEmptyHint>
                  ) : (
                    (q.matrixAnswers || []).map((cell, cellIdx) => (
                      <React.Fragment key={`${q.id}-${cell.rowId}`}>
                        {cellIdx > 0 ? <SurveyMatrixDivider /> : null}
                        <SurveyMatrixRow>
                          <SurveyMatrixLabel>{cell.rowLabel}</SurveyMatrixLabel>
                          <SurveyMatrixCells>
                            {scaleValues.map((n) => (
                              <SurveyScaleCell
                                key={`${q.id}-${cell.rowId}-${n}`}
                                $selected={String(n) === String(cell.value)}
                              >
                                {n}
                              </SurveyScaleCell>
                            ))}
                          </SurveyMatrixCells>
                        </SurveyMatrixRow>
                      </React.Fragment>
                    ))
                  )}
                </SurveyMatrixList>
              ) : null}

              {!isInfo && !isText && !isScale && !isMatrix ? (
                <OptionList>
                  {visibleOptions.length === 0 && surveyMode ? (
                    <SurveyEmptyHint>—</SurveyEmptyHint>
                  ) : (
                    visibleOptions.map((option) => (
                      <OptionRow key={option.id}>
                        {option.imageUrl ? (
                          <OptionImageCell>
                            <OptionImage src={option.imageUrl} alt="" loading="lazy" />
                          </OptionImageCell>
                        ) : null}
                        <OptionCard $tone={surveyMode ? 'neutral' : option.tone}>
                          {q.answerMode === 'single' ||
                          kind === 'dropdown' ||
                          kind === 'single' ? (
                            <SavedOptionMark
                              type="radio"
                              name={`attempt-${q.id}`}
                              checked={option.selected}
                              readOnly
                              tabIndex={-1}
                              aria-hidden
                            />
                          ) : (
                            <UiCheckboxWrap>
                              <CommonCheckbox
                                checked={option.selected}
                                disabled
                                onClick={() => {}}
                              />
                            </UiCheckboxWrap>
                          )}
                          <div>
                            <OptionTitle>{option.title}</OptionTitle>
                            {option.description.trim() !== '' ? (
                              <OptionDescription>{option.description}</OptionDescription>
                            ) : null}
                          </div>
                        </OptionCard>
                      </OptionRow>
                    ))
                  )}
                </OptionList>
              ) : null}

              {!surveyMode && q.explanation && q.explanation.trim() !== '' ? (
                <QuestionExplanation>{q.explanation}</QuestionExplanation>
              ) : null}
            </QuestionCard>
          )
        })}
      </QuestionsStack>
    </>
  )
}
